import { useState, useCallback } from 'react';
import { searchTaxByProperty, checkTaxStatus as checkTaxStatusApi } from '../services/taxApi';
import { createTransactionRecord, saveTransaction } from '../services/datatraceTransactions';

/**
 * Custom hook for managing tax search workflow
 * Tax searches are ALWAYS async - there is no synchronous tax product
 * Saves all transactions to persistent history for auditing (same as DataTrace API Tester)
 */
export const useTaxSearch = () => {
  const [taxSearchStatus, setTaxSearchStatus] = useState('not-started'); // not-started, pending, polling, completed, needs-review, error
  const [taxSearchData, setTaxSearchData] = useState(null);
  const [taxSearchError, setTaxSearchError] = useState(null);
  const [taxTransactionId, setTaxTransactionId] = useState(null);
  const [isPolling, setIsPolling] = useState(false);

  /**
   * Start a new tax search
   * @param {Object} searchParams - Search parameters (address, city, state, zip, county, apn)
   * @returns {Promise<Object>} - Search response with transactionId for polling
   */
  const startTaxSearch = useCallback(async (searchParams) => {
    setTaxSearchStatus('pending');
    setTaxSearchError(null);
    setTaxSearchData(null);

    try {
      const response = await searchTaxByProperty(searchParams);

      // Tax is ALWAYS async - extract transaction ID from metadata
      const transactionId = response.metadata?.transactionId || response.rawResponse?.transaction_id;
      
      if (!transactionId) {
        throw new Error('No transaction ID received from tax search API');
      }

      setTaxTransactionId(transactionId);
      setTaxSearchStatus('polling');
      setTaxSearchData({
        transactionId: transactionId,
        confirmationNumber: response.metadata?.confirmationNumber || response.rawResponse?.confirmation_number,
        status: response.metadata?.reportStatus || response.rawResponse?.report_status || 'PENDING',
        timestamp: new Date().toISOString(),
        searchParams
      });

      // Save to transaction history
      try {
        const productConfig = {
          productCode: 'tax',
          productType: 'taxsource',
          endpoint: 'tax'
        };
        const transaction = createTransactionRecord(
          searchParams,
          productConfig,
          { data: response.rawResponse }, // Wrap in same format as API response
          searchParams // Request body
        );
        await saveTransaction(transaction);
        console.log('Tax search transaction saved to history:', transaction.transactionId);
      } catch (saveError) {
        console.error('Failed to save tax transaction to history:', saveError);
        // Don't fail the search if transaction save fails
      }

      return response;
    } catch (error) {
      console.error('Tax search error:', error);
      setTaxSearchStatus('needs-review');
      setTaxSearchError(error.message || 'Failed to initiate tax search');
      setTaxSearchData({ error: true, errorMessage: error.message });
      throw error;
    }
  }, []);

  /**
   * Check the status of a pending tax search transaction
   * @param {string} transactionId - Optional transaction ID (uses current if not provided)
   * @returns {Promise<Object>} - Status response
   */
  const checkTaxStatus = useCallback(async (transactionId = null) => {
    const txnId = transactionId || taxTransactionId;

    if (!txnId) {
      throw new Error('No transaction ID available for status check');
    }

    setIsPolling(true);

    try {
      const statusResponse = await checkTaxStatusApi(txnId);

      // Update data with latest status
      setTaxSearchData(prev => ({
        ...prev,
        status: statusResponse.status,
        statusResponse: statusResponse,
        lastChecked: new Date().toISOString()
      }));

      // Check if completed
      const status = (statusResponse.status || '').toLowerCase().trim();
      const isCompleted = status.includes('completed') ||
                         status.includes('success') ||
                         status.includes('available') ||
                         status.includes('fulfilled');

      if (isCompleted) {
        setTaxSearchStatus('completed');
        // If response includes the actual report data, save it
        if (statusResponse.data || statusResponse.report) {
          setTaxSearchData(prev => ({
            ...prev,
            reportData: statusResponse.data || statusResponse.report
          }));
        }
      } else if (status.includes('error') || status.includes('failed')) {
        setTaxSearchStatus('needs-review');
        setTaxSearchError(statusResponse.message || 'Tax search failed');
      } else {
        setTaxSearchStatus('polling');
      }

      setIsPolling(false);
      return statusResponse;
    } catch (error) {
      console.error('Tax status check error:', error);
      setIsPolling(false);
      setTaxSearchError(error.message || 'Failed to check tax search status');
      throw error;
    }
  }, [taxTransactionId]);

  /**
   * Get the report for a completed tax search
   * @param {string} transactionId - Optional transaction ID (uses current if not provided)
   * @param {string} productCode - Product code for the report
   * @returns {Promise<Object>} - Report data
   */
  const getTaxReport = useCallback(async (transactionId = null, productCode = 'tax') => {
    const txnId = transactionId || taxTransactionId;

    if (!txnId) {
      throw new Error('No transaction ID available for report retrieval');
    }

    try {
      // Import getReport from taxApi if it exists
      const { getReport } = await import('../services/taxApi');
      const reportData = await getReport(txnId, productCode);

      setTaxSearchData(prev => ({
        ...prev,
        reportData
      }));

      return reportData;
    } catch (error) {
      console.error('Tax report retrieval error:', error);
      setTaxSearchError(error.message || 'Failed to retrieve tax report');
      throw error;
    }
  }, [taxTransactionId]);

  /**
   * Reset the tax search state
   */
  const resetTaxSearch = useCallback(() => {
    setTaxSearchStatus('not-started');
    setTaxSearchData(null);
    setTaxSearchError(null);
    setTaxTransactionId(null);
    setIsPolling(false);
  }, []);

  return {
    // State
    taxSearchStatus,
    taxSearchData,
    taxSearchError,
    taxTransactionId,
    isPolling,

    // Actions
    startTaxSearch,
    checkTaxStatus,
    getTaxReport,
    resetTaxSearch
  };
};

export default useTaxSearch;
